import { type ComponentPropsWithoutRef, useId, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'next-i18next';
import IcCloseCircleFill from 'src/assets/images/icCloseCircleFill.svg';
import IcWarningFill from 'src/assets/images/icWarningFill.svg';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
const Form = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;
export const InputBase = ({
  theme
}: {
  theme: Theme;
}) => css`
  all: unset;
  box-sizing: border-box;

  flex: 1;

  background-color: ${theme.color.surface4};
  color: ${theme.color.contentDeep};

  border-width: 1px;
  border-style: solid;
  border-color: ${theme.color.surface4};
  border-radius: 8px;

  transition: border-color 0.15s ease-out;

  :focus {
    border-color: ${theme.color.primary1};
  }

  ::placeholder {
    font-size: 15px;
    font-weight: 400;

    color: ${theme.color.content8};
  }

  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    -webkit-text-fill-color: ${theme.color.contentDeep};
    -webkit-box-shadow: 0 0 0px 1000px inherit inset;
    transition: background-color 9999s ease-in-out 0s; // 자동완성때문에 인풋창이 노란색으로 변하는걸 막기 위해 delay
  }

  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
`;
const StyledInput = styled.input`
  ${InputBase}

  /** 6px (top padding) + 15px (label's height) + 2px (gap between input and label) */
  padding: ${6 + 15 + 2}px 15px 6px;

  line-height: 15px;
  height: 56px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 0px;
    padding-block-end: 4px;
    padding-inline-end: 32px;

    border: none;
    border-radius: 0;

    font-size: 24px;
    font-weight: 400;

    border-bottom: 1px solid ${({
  theme
}) => theme.color.surface5};

    background-color: transparent;
    color: ${({
  theme
}) => theme.color.content12};

    :focus {
      border-bottom: 1px solid ${({
  theme
}) => theme.color.surface5};
    }

    ::placeholder {
      color: ${({
  theme
}) => theme.color.content8};
      font-size: 24px;
    }

    :-webkit-autofill,
    :-webkit-autofill:hover,
    :-webkit-autofill:focus {
      -webkit-text-fill-color: ${({
  theme
}) => theme.color.primary1};
    }
  }
`;
export const Label = styled.label<{
  isFocus: boolean;
}>`
  all: unset;
  box-sizing: border-box;

  position: absolute;
  z-index: 1;
  inset-block-start: 17px;
  inset-inline-start: 16px;

  color: ${({
  theme
}) => theme.color.content9};

  transition:
    transform 0.15s ease-out,
    font-size 0.15s ease-out;

  font-size: 16px;
  font-weight: 400;

  ${({
  isFocus
}) => isFocus && `
      transform: translateY(-10px);
      font-size: 12px;
    `}

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: none;
  }
`;
const ResetButton = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;

  position: absolute;
  inset-block-start: calc((28px - 24px) / 2);
  inset-inline-end: 0px;

  width: 24px;
  height: 24px;

  display: none;
  align-items: center;
  justify-content: center;

  color: ${({
  theme
}) => theme.color.content6};

  ${({
  theme
}) => theme.screenSize.mobile} {
    display: inline-flex;
  }

  > svg {
    width: 24px;
    height: 24px;
  }
`;
export const ErrorRow = styled.div`
  margin-block-start: 10px;

  display: flex;
  align-items: flex-start;
  gap: 8px;

  color: ${({
  theme
}) => theme.color.accentA1};

  > svg {
    width: 16px;
    height: 16px;
  }
`;
export const ErrorText = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  a {
    color: inherit;
  }
`;
export interface InputProps extends Omit<ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'> {
  value: string;
  onChangeText: (text: string) => void;
  label: string;
  errorMessage?: string;
}
export const ErrorMessage: React.FC<{
  message: string;
}> = ({
  message
}) => {
  const isMobile = useCssMediaDevice() === 'mobile';
  const {
    t
  } = useTranslation();
  return <ErrorRow style={{
    ...(isMobile && {
      display: 'none'
    })
  }} data-sentry-element="ErrorRow" data-sentry-component="ErrorMessage" data-sentry-source-file="index.tsx">
      <IcWarningFill data-sentry-element="IcWarningFill" data-sentry-source-file="index.tsx" />
      <ErrorText dangerouslySetInnerHTML={{
      __html: t(message)
    }} data-sentry-element="ErrorText" data-sentry-source-file="index.tsx"></ErrorText>
    </ErrorRow>;
};
const Input = ({
  label,
  placeholder,
  value,
  onFocus,
  onBlur,
  type,
  errorMessage,
  onChangeText,
  className,
  style,
  ...props
}: InputProps) => {
  const isMobile = useCssMediaDevice() === 'mobile';
  const inputId = useId();
  const [isFocus, setIsFocus] = useState(false);
  return <Form className={className} style={style} data-sentry-element="Form" data-sentry-component="Input" data-sentry-source-file="index.tsx">
      <Label htmlFor={inputId} isFocus={isFocus || !!value} data-sentry-element="Label" data-sentry-source-file="index.tsx">
        {label}
      </Label>
      <StyledInput {...props} id={inputId} onFocus={e => {
      setIsFocus(true);
      onFocus?.(e);
    }} onBlur={e => {
      setIsFocus(false);
      onBlur?.(e);
    }} onChange={e => onChangeText(e.target.value)} type={type} value={value} placeholder={isFocus || isMobile ? placeholder : ''} data-sentry-element="StyledInput" data-sentry-source-file="index.tsx" />
      {!!value && <ResetButton type='button' onClick={() => {
      onChangeText('');
    }}>
          <IcCloseCircleFill />
        </ResetButton>}
      {errorMessage && <ErrorMessage message={errorMessage} />}
    </Form>;
};
export default Input;